/**
 * Legacy-Redirect fuer /kunden/:id → /admin/adressbuch?tab=contacts&contactId=<x>
 *
 * P4.3 (2026-07-06): Die Kundenakte lebt nun eingebettet im Adressbuch-
 * Kontakt-Detail (CustomerAkteEmbeddedPanel). Diese Route existiert nur noch,
 * damit bestehende Aufrufer und Bookmarks aus Auftraegen, Baustellen, Kalender,
 * KundenStammPage etc. weiterhin funktionieren — sie werden auf den neuen
 * Ort umgeleitet.
 *
 * Ablauf:
 *  1. customer_id via /adressbuch/customer/:customerId/contact-bridge in
 *     contact_id ueberfuehren
 *  2. Navigate zu /admin/adressbuch?tab=contacts&contactId=<contactId>&tab=<origTab>
 *
 * Bei fehlender Bridge (Alt-Datensatz ohne Kontakt): freundlicher Fehler +
 * Zurueck-Link zum Kunden-Listing.
 */
import { useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';

interface BridgeResponse {
  success: boolean;
  data?: { contact_id: string };
  error?: string;
}

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [contactId, setContactId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let aborted = false;
    api.get<BridgeResponse>(`/adressbuch/customer/${id}/contact-bridge`)
      .then((r) => {
        if (aborted) return;
        if (r.success && r.data?.contact_id) setContactId(r.data.contact_id);
        else setError(r.error ?? 'Keine Bridge gefunden');
      })
      .catch((e: unknown) => {
        if (aborted) return;
        const status = (e as { status?: number }).status;
        if (status === 404) setError('Kunde nicht gefunden');
        else setError('Fehler beim Weiterleiten');
      });
    return () => { aborted = true; };
  }, [id]);

  if (contactId) {
    const params = new URLSearchParams();
    params.set('tab', 'contacts');
    params.set('contactId', contactId);
    const origTab = searchParams.get('tab');
    if (origTab) params.set('tab', origTab);
    // 'tab' wird ueberladen: zuerst der Ziel-Adressbuch-Tab (contacts), dann
    // ggf. der Kundenakte-Sub-Tab. In der Adressbuch-Page verzweigen wir
    // beide aus searchParams — das ist bewusst, damit die alte URL ihre
    // ?tab=akten-Semantik behaelt.
    if (origTab) params.set('adressbuchTab', 'contacts');
    return <Navigate to={`/admin/adressbuch?${params.toString()}`} replace />;
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Link to="/admin/adressbuch" className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-4">
          <ArrowLeft className="w-4 h-4" /> Zum Adressbuch
        </Link>
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
          <div className="mt-2 text-xs text-red-200/70">
            Die Kundenakte liegt seit 2026-07-06 im Adressbuch (Reiter Kontakte). Diese Legacy-URL findet
            keine Zuordnung — bitte oeffnen Sie den Kunden dort direkt.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-20 gap-2 text-muted-foreground text-sm">
      <Loader2 className="w-5 h-5 animate-spin" /> Weiterleitung…
    </div>
  );
}
